<template>
  <div>
    <v-toolbar elevation="0" class="mb-3 px-2 webhook-deliveries-toolbar" rounded>
      <v-select
        :model-value="statusFilter"
        :items="statusFilterItems"
        :label="$t('routes.webhookIntegrations.deliveryStatus')"
        variant="solo-filled"
        flat
        hide-details
        clearable
        class="mr-2"
        style="max-width: 220px;"
        @update:model-value="$emit('update:statusFilter', $event)"
      />
      <v-select
        :model-value="eventTypeFilter"
        :items="eventTypeFilterItems"
        :label="$t('routes.webhookIntegrations.eventType')"
        item-title="label"
        item-value="id"
        variant="solo-filled"
        flat
        hide-details
        clearable
        class="mr-2"
        style="max-width: 280px;"
        @update:model-value="$emit('update:eventTypeFilter', $event)"
      />
      <v-spacer />
      <v-btn icon variant="text" :loading="loading" @click="$emit('refresh')">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-toolbar>

    <v-data-iterator :items="deliveries" :items-per-page="20" :loading="loading" return-object>
      <template v-slot:loader>
        <v-row class="mt-6">
          <v-col class="text-center">
            <v-progress-circular indeterminate size="56" width="6" color="primary" :aria-label="$t('routes.webhookIntegrations.loadingDeliveries')" />
            <div>{{ $t('routes.webhookIntegrations.loadingDeliveries') }}</div>
          </v-col>
        </v-row>
      </template>
      <template v-slot:no-data>
        <v-row class="mt-6">
          <v-col class="text-center">{{ $t('routes.webhookIntegrations.noDeliveriesFound') }}</v-col>
        </v-row>
      </template>
      <template v-slot:default="{ items }">
        <v-card v-for="item in items" :key="item.value.id" class="mb-2">
          <v-card-text>
            <div class="d-flex flex-wrap align-start ga-3">
              <div class="webhook-delivery-main">
                <div class="font-weight-medium">{{ formatEventTypeLabel(item.value.event_type) }}</div>
                <div class="text-caption text-grey">{{ item.value.event_id }}</div>
              </div>

              <div class="webhook-delivery-meta">
                <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.status') }}</div>
                <v-chip :color="getDeliveryStatusColor(item.value.status)" size="small" variant="tonal">
                  {{ formatDeliveryStatus(item.value.status) }}
                </v-chip>
              </div>

              <div class="webhook-delivery-meta">
                <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.deliveryAttempts') }}</div>
                <div>{{ item.value.attempts || 0 }} / {{ item.value.max_attempts || '-' }}</div>
              </div>

              <div class="webhook-delivery-meta">
                <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.responseCode') }}</div>
                <div>{{ item.value.last_status_code || '-' }}</div>
              </div>

              <div class="webhook-delivery-time">
                <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.createdAt') }}</div>
                <div>{{ formatDeliveryDate(item.value.created_at) }}</div>
              </div>

              <div class="webhook-delivery-time">
                <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.lastAttemptAt') }}</div>
                <div>{{ formatDeliveryDate(item.value.last_attempt_at) }}</div>
              </div>

              <div class="webhook-delivery-time">
                <div class="text-caption text-grey">{{ $t('routes.webhookIntegrations.completedAt') }}</div>
                <div>{{ formatDeliveryDate(item.value.delivered_at || item.value.failed_at) }}</div>
              </div>

              <div class="webhook-delivery-actions">
                <v-btn size="small" variant="text" color="primary" @click="$emit('view', item.value)">
                  <v-icon start>mdi-eye-outline</v-icon>
                  {{ $t('routes.webhookIntegrations.viewDetails') }}
                </v-btn>
                <v-btn
                  v-if="canRedeliver(item.value)"
                  size="small"
                  variant="text"
                  color="warning"
                  :loading="redeliveringDeliveryId === item.value.id"
                  @click="openRedeliveryConfirm(item.value)"
                >
                  <v-icon start>mdi-reload</v-icon>
                  {{ $t('routes.webhookIntegrations.redeliver') }}
                </v-btn>
              </div>
            </div>

            <v-alert
              v-if="item.value.last_failure_message"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-3 mb-0"
            >
              {{ item.value.last_failure_message }}
            </v-alert>
          </v-card-text>
        </v-card>
      </template>
    </v-data-iterator>

    <v-dialog v-model="showRedeliveryConfirmDialog" max-width="520">
      <v-card>
        <v-card-title class="bg-warning d-flex justify-space-between">
          {{ $t('routes.webhookIntegrations.redeliverDelivery') }}
          <v-btn icon variant="text" @click="showRedeliveryConfirmDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>{{ $t('routes.webhookIntegrations.confirmRedeliverDelivery') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="warning" variant="elevated" @click="confirmRedelivery">
            {{ $t('routes.webhookIntegrations.redeliver') }}
          </v-btn>
          <v-btn variant="text" @click="showRedeliveryConfirmDialog = false">
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  DELIVERY_STATES,
  EVENT_GROUPS,
  formatDateTime,
  getDeliveryStatusColor,
  getEventTranslationKey,
} from '@/routes/console/webhook-integrations/WebhookConstants.js';

export default {
  name: 'WebhookDeliveriesList',
  props: {
    deliveries: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    statusFilter: {
      type: String,
      default: null,
    },
    eventTypeFilter: {
      type: String,
      default: null,
    },
    redeliveringDeliveryId: {
      type: String,
      default: null,
    },
  },
  emits: ['refresh', 'update:statusFilter', 'update:eventTypeFilter', 'view', 'redeliver'],
  data() {
    return {
      showRedeliveryConfirmDialog: false,
      redeliveryCandidate: null,
    };
  },
  computed: {
    statusFilterItems() {
      return DELIVERY_STATES.map((status) => ({
        title: this.formatDeliveryStatus(status),
        value: status,
      }));
    },
    eventTypeFilterItems() {
      return EVENT_GROUPS.flatMap((group) => group.events).map((eventType) => ({
        id: eventType,
        label: this.formatEventTypeLabel(eventType),
      }));
    },
  },
  methods: {
    getDeliveryStatusColor,
    formatDeliveryDate(dateString) {
      return formatDateTime(dateString) || this.$t('routes.webhookIntegrations.noDeliveryTimestamp');
    },
    formatDeliveryStatus(status) {
      return this.$t(`routes.webhookIntegrations.deliveryStates.${status}`);
    },
    formatEventTypeLabel(eventType) {
      return this.$t(`routes.webhookIntegrations.events.${getEventTranslationKey(eventType)}`);
    },
    canRedeliver(delivery) {
      return delivery.status === 'failed';
    },
    openRedeliveryConfirm(delivery) {
      this.redeliveryCandidate = delivery;
      this.showRedeliveryConfirmDialog = true;
    },
    confirmRedelivery() {
      if (!this.redeliveryCandidate) return;
      this.$emit('redeliver', this.redeliveryCandidate);
      this.showRedeliveryConfirmDialog = false;
      this.redeliveryCandidate = null;
    },
  },
}
</script>

<style scoped lang="scss">
.webhook-deliveries-toolbar {
  gap: 8px;
}

.webhook-delivery-main {
  min-width: 220px;
  flex: 1 1 260px;
}

.webhook-delivery-meta {
  min-width: 110px;
  flex: 0 1 140px;
}

.webhook-delivery-time {
  min-width: 160px;
  flex: 0 1 190px;
}

.webhook-delivery-actions {
  flex: 0 0 auto;
}

@media only screen and (max-width: 600px) {
  .webhook-deliveries-toolbar {
    display: block;
  }
}
</style>
